import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { VenueService } from '../shared/venue.service';
import { VenueModel } from '../shared/venue.model';
import { NotificationService } from '@app/shared/notification/notification.service';
import { CommonModule } from '@angular/common';
import { FullCalendarModule } from '@fullcalendar/angular';
import { ArrowLeft, LucideAngularModule } from 'lucide-angular';
import { ButtonComponent } from '@app/partials/form/button/button.component';

@Component({
  selector: 'app-venue-prices-calendar',
  standalone: true,
  imports: [CommonModule, FullCalendarModule, LucideAngularModule, ButtonComponent, RouterLink],
  templateUrl: './venue-prices-calendar.component.html',
  styleUrl: './venue-prices-calendar.component.css',
})
export class VenuePricesCalendarComponent implements OnInit {
  venue: VenueModel | null = null;
  protected readonly BackIcon = ArrowLeft;

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    weekends: true,
    firstDay: 0, // Start week on Sunday (0=Sunday, 1=Monday)
    locale: 'en-gb', // Use UK locale
    timeZone: 'UTC', // Use UTC to avoid timezone shifts
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth', // Removed dayGridWeek since we're focusing on full-day view
    },
    dayMaxEvents: false, // Disable event limiting to show all prices
    events: [],
    eventContent: this.renderEventContent.bind(this),
    displayEventTime: false, // Hide event time since these are full-day events
    eventDisplay: 'block', // Make events take full width
  };

  constructor(
    private route: ActivatedRoute,
    private venueService: VenueService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.data['venue']) {
      this.venue = this.route.snapshot.data['venue'];
      this.loadVenuePrices();
    }
  }

  loadVenuePrices(): void {
    if (!this.venue?.id) {
      this.notificationService.error('Venue not found');
      return;
    }

    this.venueService.getVenueDailyPrices(this.venue.id).subscribe({
      next: (response) => {
        const events = response.data.map((priceData) => ({
          title: ``,
          start: priceData.date,
          allDay: true,
          classNames: ['price-event'],
          extendedProps: {
            minimum_spend: priceData.minimum_spend,
            hire_price: priceData.hire_price,
          },
        }));

        this.calendarOptions = {
          ...this.calendarOptions,
          events,
        };
      },
      error: (error) => {
        this.notificationService.error('Failed to load venue prices');
      },
    });
  }

  renderEventContent(eventInfo: any): any {
    const { minimum_spend, hire_price } = eventInfo.event.extendedProps;
    const hireDisplay = this.formatPrice(hire_price);
    const minimumSpendDisplay = this.formatPrice(minimum_spend);
    return {
      html: `
      <div class="flex flex-col p-1 w-full h-full">
        <div class="text-xs">
          <div class="price-row">
            <span class="font-semibold">Hire:</span>
            <span>${hireDisplay}</span>
          </div>
          <div class="price-row">
            <span class="font-semibold">Min Spend:</span>
            <span>${minimumSpendDisplay}</span>
          </div>
        </div>
      </div>
    `,
    };
  }

  renderDayCellContent(dayInfo: any): any {
    return { html: `<span>${dayInfo.dayNumberText}</span>` };
  }

  navigateBack(): void {
    window.history.back();
  }

  private formatPrice(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '&ndash;';
    }

    const numeric = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(numeric)) {
      return '&ndash;';
    }

    const formatted = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numeric);

    return formatted.replace('£', '&pound;');
  }
}
